<modal-panel
  [title]="(isEditing ? 'Edit Manual ERP Update' : 'Add Manual ERP Update') + ' — SO ' + data.salesOrderId + (data.tripReference ? ' (' + data.tripReference + ')' : '')"
  data-testid="tmt-erp-manual-update-dialog"
  (close)="cancel()">

  @if (loading) {
    <div style="display:flex; justify-content:center; padding: 32px;">
      <mat-spinner diameter="36"/>
    </div>
  } @else {
    <p class="manual-update__hint">
      Use this when the sales order was sent to ERP outside FMS (e.g. sent manually in TMT/D365
      because ERP outbound isn't enabled in this environment). Fill in the sales order number and
      timestamp you got from ERP so FMS closes out the pending change here and captures the next
      TMT change as a fresh delta.
    </p>

    @if (isEditing) {
      <p class="manual-update__hint">
        <strong>Editing an existing manual entry.</strong> Made a mistake last time? Fix the
        fields below and save — the previous entry is replaced, not duplicated.
      </p>
    }

    <mat-expansion-panel>
      <mat-expansion-panel-header>
        <mat-panel-title>How this works &amp; what each field affects</mat-panel-title>
      </mat-expansion-panel-header>
      <ul class="manual-update__info">
        <li>
          <strong>Saving does two things, in order:</strong> (1) marks every currently pending
          (not-yet-synced) row in the TMT sync queue for this sales order as sent — this is the
          part that actually resets the delta tracking, so the next real TMT change starts a fresh
          delta instead of piling onto this one; (2) writes an ERP response record against that
          same queue row, purely for reporting/timeline visibility. If step 1 succeeds but step 2
          fails (or vice versa), re-open this dialog and save again to reconcile — check the
          report grid/timeline afterward either way.
        </li>
        <li>
          <strong>ERP Sales Order Number</strong> — the real D365 sales order number ERP assigned.
          Only used for reporting/search (no delta-logic impact), but it's the actual proof a real
          order exists in ERP, so put the genuine number, not a placeholder.
        </li>
        <li>
          <strong>Sent to ERP (timestamp)</strong> — stamped on both the sync-queue row and the
          response; drives the ERP report's date-range filter and the sync timeline's ordering. An
          inaccurate timestamp won't break the delta reset itself, but will misplace this entry in
          date-filtered reports/timelines.
        </li>
        <li>
          <strong>Result</strong> — mirrors what a real ERP response would report (SUCCESS /
          PENDING / ERROR). Note the record's internal ApplyStatus is always set to "Pending"
          regardless of what you choose here — that's a separate internal processing flag, not
          tied to this Result value.
        </li>
        <li>
          <strong>ERP Message Id</strong> — an internal FMS correlation id, not the sales order
          number. It also decides which row gets written: if it matches an existing response row
          that's still "PENDING", saving overwrites that row instead of creating a new one — leave
          the auto-generated value unless you deliberately mean to update a specific existing
          record.
        </li>
        <li>
          <strong>Full send</strong> — only matters if there are pending rows marked FAILED. Off:
          those rows are skipped entirely (left pending/failed for retry). On: they're included and
          marked resolved as part of this save — only do this if this entry genuinely accounts for
          them, since nothing about the underlying failure is actually being fixed by checking this
          box.
        </li>
        <li>
          <strong>Editing:</strong> reopening this dialog on a row that already has a response
          preloads its values and reuses the same ERP Message Id. Saving deletes the old response
          row first, then writes a fresh one — so a correction always fully replaces the previous
          entry instead of leaving a stale duplicate behind, no matter what Result it had.
        </li>
      </ul>
    </mat-expansion-panel>

    @if (noPendingChanges) {
      <p class="manual-update__warning">
        No pending (not-yet-synced) TMT → ERP changes were found for this sales order. Saving will
        still record the ERP response below, but no sync-queue row will be marked as sent.
      </p>
    } @else {
      <p class="manual-update__hint">
        {{ pendingCount }} pending change(s) found
        @if (pendingFailedCount > 0) {
          , including {{ pendingFailedCount }} marked FAILED.
        } @else {
          .
        }
      </p>
    }

    <div class="manual-update__fields" [formGroup]="form">
      <div class="manual-update__field">
        <mat-form-field appearance="outline">
          <mat-label>ERP Sales Order Number</mat-label>
          <input matInput formControlName="erpOrderNumber" [maxlength]="erpOrderNumberMaxLength" data-testid="tmt-erp-manual-update-order-number" />
          @if (form.get('erpOrderNumber')?.hasError('maxlength')) {
            <mat-hint>Max {{ erpOrderNumberMaxLength }} characters.</mat-hint>
          }
        </mat-form-field>
        <p class="manual-update__field-note">
          The D365 sales order number shown after you manually process the send in D365/TMT
          (e.g. the confirmation number on the sales order screen). This is the actual proof a
          real order exists in ERP — enter the genuine number, not a placeholder.
        </p>
      </div>

      <div class="manual-update__field">
        <mat-form-field appearance="outline">
          <mat-label>Sent to ERP</mat-label>
          <input matInput type="datetime-local" formControlName="erpMessageTimestamp" [max]="nowInputValue" data-testid="tmt-erp-manual-update-timestamp" />
          @if (form.get('erpMessageTimestamp')?.hasError('futureDate')) {
            <mat-hint>This timestamp is in the future.</mat-hint>
          }
        </mat-form-field>
        <p class="manual-update__field-note">
          The date/time D365 shows the sales order was actually created/processed (e.g. its
          creation timestamp on the sales order screen). Cannot be in the future — used for
          report date filters and timeline ordering, so an inaccurate value only misplaces this
          entry in those views, it does not affect the delta reset itself.
        </p>
      </div>

      <div class="manual-update__field">
        <mat-form-field appearance="outline">
          <mat-label>Result</mat-label>
          <mat-select formControlName="result" data-testid="tmt-erp-manual-update-result">
            @for (option of resultOptions; track option) {
              <mat-option [value]="option">{{ option }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>

      @if (form.get('result')?.value === 'ERROR') {
        <div class="manual-update__field">
          <mat-form-field appearance="outline">
            <mat-label>Error Description</mat-label>
            <textarea matInput formControlName="errorDescription" rows="3" data-testid="tmt-erp-manual-update-error-description"></textarea>
          </mat-form-field>
          <p class="manual-update__field-note">Required when Result is ERROR.</p>
        </div>
      }

      <div class="manual-update__field">
        <mat-form-field appearance="outline">
          <mat-label>ERP Message Id</mat-label>
          <input matInput formControlName="erpMessageId" [readonly]="isEditing" data-testid="tmt-erp-manual-update-message-id" />
          @if (form.get('erpMessageId')?.hasError('invalidGuid')) {
            <mat-hint>Must be a valid GUID.</mat-hint>
          }
        </mat-form-field>
        <p class="manual-update__field-note">
          @if (isEditing) {
            Reused from the entry being edited so saving replaces it rather than creating a
            duplicate. Only change this if you deliberately want to correlate to a different
            in-flight Service Bus message.
          } @else {
            Not shown in D365/TMT screens — if correlating to a specific in-flight message, copy
            the MessageId from the Azure Portal's Service Bus Explorer for the ERP outbound
            queue/topic. Otherwise leave the auto-generated value.
          }
        </p>
        @if (!isEditing) {
          <button mat-button type="button" (click)="regenerateMessageId()">Regenerate Id</button>
        }
      </div>

      <div class="manual-update__field">
        <mat-checkbox formControlName="isFullSend">
          Full send (also resolves pending FAILED rows — only check if this manual entry truly
          covers them; leaving unchecked skips failed rows so they stay pending for retry)
        </mat-checkbox>
        @if (pendingFailedCount > 0 && !form.get('isFullSend')?.value) {
          <p class="manual-update__warning">
            {{ pendingFailedCount }} pending FAILED row(s) will NOT be marked as sent unless "Full
            send" is checked.
          </p>
        }
      </div>
    </div>
  }

  <ng-container modalPanelFooter>
    <button
      mat-raised-button
      color="primary"
      type="submit"
      data-testid="tmt-erp-manual-update-submit-button"
      (click)="submit()"
      [disabled]="loading || saving || form?.invalid">
      @if (saving) {
        <mat-spinner diameter="20"/>
      } @else {
        Save
      }
    </button>
    <button mat-button (click)="cancel()">Cancel</button>
  </ng-container>
</modal-panel>
